import { Injectable } from '@angular/core';
import { v1 as uuid } from 'uuid';

import { CommonRequestService } from './common-request.service';
import { CommonUtilityService } from './common-utility';
import { AppSettings } from '../app.settings';
import { RequestOptions, URLSearchParams } from '@angular/http';
import { UserService } from './user.service';
import { LBSymbologyType } from './symbologys.service';
import { SystemAttribute, NinjaDefaults } from './settings-ninja.service';

interface Dict {
	subLevels: { [levelName: string]: number };
	signUpStates: any;
	authTypes: any;
	uom: {
		[uom: string]: number;
	};
}

export interface MapSpecification {
	basemap: BasemapSpec[];
	cursor: string;
	extent: {
		xmin: number;
		ymin: number;
		xmax: number;
		ymax: number;
	};
	srs: string;
}
export interface BasemapSpec {
	name: string;
	description: string;
	url: string;
	type: string;
}

export type LbColumnType = 'number' | 'text' | 'date' | 'bool' | 'jsonb' | 'xml' | 'USER-DEFINED';

export interface DataColumn {
	name: string; // column name
	heading: string; // column displayed name
	width: number; // width in pixel
	type: LbColumnType; // column type
	sourceTab: string; // source tab
	align: 'C' | 'L' | 'R'; // text-align. 'C' for center, 'L' for left, 'R' for right
	isDisplay: boolean; // display or hide column. 'Y' | 'N'
	isPrimeKey: boolean; // is prime key or not
	isGto: boolean; // ???
	seq: number; // column sequence for displaying
}

// DO NOT CHANGE IT. If this does need to change, make sure the update functions also get changed
export interface AppinfoDefaults {
	arnoldQc: {};
	lrs: {
		lrm: string;
		measurePrecision: number,
		measureUom: string;
	};
	mapSpec: MapSpecification;
	misc: {
		subLengthUom: 'month' | 'year';
		subLevel: 'PROFESSIONAL' | 'ENTERPRISE' | 'BASIC';
		symbologyType: LBSymbologyType;
	};
}

export interface CustomerAppinfoLBE {
	defaults: AppinfoDefaults;
}
export interface CustomerAppinfoNinja {
	defaults: NinjaDefaults;
	customerProfile;
}

export interface USState {
	state: string;
	stateFp: string;
	code: string;
	bbox: string; // e.g. "-88.473227,30.223334,-84.88908,35.008028"
}

export type BaseMapType = 'OpenStreet Map' | 'Google Map' | 'ArcGIS Online' | 'Bing Map' | 'user_defined';

@Injectable()
export class AppinfoService {
	public cursors: any[];
	public basemapTypes: BaseMapType[];
	// public mapSpecs: MapSpecification;

	public dict: Dict = {
		subLevels: null,
		signUpStates: null,
		authTypes: null,
		uom: {}
	};

	// This defaults should !!NOT!! combine defaults from appinfo & appinfo-customer
	// Since there could be more than one applications in portal like HPMS, LBE
	public defaults: AppinfoDefaults;

	public columnMap: {
		[colName: string]: DataColumn
	} = {};

	// HPMS, LBE properties
	public layerLoadMap: any = {};

	// AZGIV
	public usStates: USState[];

	// UUID which will be used as session id
	// This id will be generated at azgiv component init, and removed at logging out
	private sessionUuid: string;

	constructor(
		private requestService: CommonRequestService,
		private utilityService: CommonUtilityService,
		private userService: UserService
	) {}
	// Get information needed before login.
	getAppinfo(): Promise<any> {
		console.log('Service Called: getAppInfo.');
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/appInfo';
			const params: URLSearchParams = new URLSearchParams();
			const options: RequestOptions = new RequestOptions();
			params.set('applications', JSON.stringify([AppSettings.lbeAppName, AppSettings.hpmsAppName, AppSettings.ninjaAppName]));
			options.params = params;
			this.requestService.requestGet(url, options,
				(rsp) => {
					try {
						this.setDict(rsp.data.dict);
						this.setColumnMap(rsp.data.gdo);
						this.setLayerLoadMap(rsp.data.lbo_ui_layer_load);
						this.setMapDict(rsp.data);
						this.defaults = this.transformDefaults(rsp.data.default_values);
						resolve();
					} catch (err) {
						reject(err);
					}
				}, (err) => {
					reject();
				});
		});
	}
	async getCustomerAppInfoLBE() {
		try {
			const rsp = await this.getCustomerAppInfo(AppSettings.lbeAppName);
			const info: CustomerAppinfoLBE = {
				defaults: this.transformDefaults(rsp.default_values)
				// TODO: other properties in customer appinfo
			};
			return info;
		} catch (err) {
			console.log(err);
		}
	}
	async getCustomerAppInfoNinja(): Promise<CustomerAppinfoNinja> {
		try {
			const rsp = await this.getCustomerAppInfo(AppSettings.ninjaAppName);
			const info: CustomerAppinfoNinja = {
				defaults: rsp.default_values ? JSON.parse(rsp.default_values) : {},
				customerProfile: rsp.customer_profile
				// TODO: other properties in customer appinfo
			};
			return info;
		} catch (err) {
			console.log(err);
		}
	}
	getCustomerAppInfo(application: string) {
		console.log('Service Called: getCustomerAppInfo.');
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/customerAppInfo';
			const params: URLSearchParams = new URLSearchParams();
			const options: RequestOptions = new RequestOptions();
			params.set('application', application);
			params.set('customerId', this.userService.getUserInfo().customerId.toString());
			options.params = params;
			this.requestService.requestGet(url, options,
				(rsp) => {
					try {
						const data = rsp.data || {}; // TODO: data[0] ask Bo
						resolve(data);
					} catch (err) {
						reject(err);
					}
				}, (err) => {
					reject(err);
				});
		});
	}

	getSessionUuid() {
		return this.sessionUuid;
	}
	generateSessionUuid() {
		this.sessionUuid = uuid();
	}

	// getAppinfo(successCallback?, errorCallback?) {
	// 	console.log('Service Called: getAppInfo.');
	// 	const url = AppSettings.serviceRootUrl + 'admin/appInfo';
	// 	return this.requestService.requestGet(url, null,
	// 		(rsp) => {
	// 			this.setDict(rsp.data.dict);
	// 			if (successCallback) {
	// 				successCallback();
	// 			}
	// 		}, (err) => {
	// 			if (errorCallback) {
	// 				errorCallback();
	// 			}
	// 		});
	// }
	private setDict(data) {
		if (!data) {
			return;
		}
		const dataObj: any = this.utilityService.arrayToObjectByFirstProp(data);
		this.dict.subLevels = this.utilityService.arrayToObjectByFirstProp(dataObj.SUBSCRIPTION_LEVEL);
		this.dict.signUpStates = this.utilityService.arrayToObjectByFirstProp(dataObj.SIGNUP_STATE);
		this.dict.authTypes = this.utilityService.arrayToObjectByFirstProp(dataObj.AUTHENT_TYPE);
		this.dict.uom = this.utilityService.arrayToObjectByFirstProp(dataObj.UOM);
	}

	private setColumnMap(data: {count: number, result: any[]}): void {
		if (!data) {
			console.log('No Gdo Info.');
			return;
		}
		// convert Gdo data to columnMap
		for (let i = 0; i < data.result.length; i++) {
			this.columnMap[data.result[i].col_name] = {
				name: data.result[i].col_name, // column name
				heading: data.result[i].col_heading, // column displayed name
				width: data.result[i].col_size,
				type: data.result[i].col_type,
				sourceTab: data.result[i].src_tab, // source tab
				align: data.result[i].align, // text-align. 'C' for center, 'L' for left, 'R' for right
				isDisplay: data.result[i].display === 'Y' ? true : false , // display or hide column. 'Y' | 'N'
				isPrimeKey: data.result[i].prim_key === 'Y' ? true : false, // is prime key or not
				isGto: data.result[i].gto === 'Y' ? true : false, // ???
				seq: data.result[i].col_seq // column sequence
			};
		}
	}

	// Set basemap list and curor list
	private setMapDict(data) {
		this.basemapTypes = data.basemap_type ? JSON.parse(data.basemap_type) : [];
		this.cursors = data.cursor_collection ? JSON.parse(data.cursor_collection) : [];
	}
	// Transform default values obtained from appinfo or customer appinfo
	private transformDefaults(data): AppinfoDefaults {
		const lrsKey = 'lrs';
		const mapSpecKey = 'mapSpec';
		const arnoldQcKey = 'arnoldQC';
		const miscKey = 'misc';
		data = data ? JSON.parse(data) : {};
		data[lrsKey] = data[lrsKey] || {};
		data[mapSpecKey] = data[mapSpecKey] || {};
		data[arnoldQcKey] = data[arnoldQcKey] || {};
		data[miscKey] = data[miscKey] || {};
		const defaults: AppinfoDefaults = {
			lrs: {
				lrm: data[lrsKey].lrm,
				measurePrecision: data[lrsKey].measurePrecision,
				measureUom: data[lrsKey].measureUom
			},
			mapSpec: {
				basemap: data[mapSpecKey].basemap,
				cursor: data[mapSpecKey].cursor,
				extent: data[mapSpecKey].extent,
				srs: data[mapSpecKey].srs
			},
			arnoldQc: {}, // TODO
			misc: data.misc
		};
		return defaults;
	}
	// Join defaults values
	// SouceDefaults value have higher priority
	joinDefaults(targetDefaults: AppinfoDefaults, sourceDefaults) {
		const merge = (objTarget, objSource) => { // Merge properties (first level) of objSource into objTarget
			for (const prop of Object.keys(objSource)) {
				if (objSource[prop] || objSource[prop] === 0) {
					objTarget[prop] = objSource[prop];
				}
			}
		};
		merge(targetDefaults.lrs, sourceDefaults.lrs);
		merge(targetDefaults.mapSpec, sourceDefaults.mapSpec);
		merge(targetDefaults.misc, sourceDefaults.misc);
		// TODO: arnoldQc & misc
		return targetDefaults;
	}

	// Update the json object for customerAppinfo. (if not exist, it will be added. this logic is implemented in database)
	updateDefaultValue(defaults, appName: string, customerId?: number, skipAuth?: boolean): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/defaultValue';
			const body = {
				customerId: customerId || this.userService.getUserInfo().customerId,
				defaultValue: JSON.stringify(defaults),
				application: appName
			};
			this.requestService.requestPut(url, body,
				(rsp) => {
					resolve();
				}, (err) => {
					reject(err);
				}, skipAuth);
		});
	}

// HPMS, LBE information --------------------
	private setLayerLoadMap(data) {
		if (!data) {
			console.log('No lbo_ui_layer_load Info.');
			return;
		}
		data = JSON.parse(data) || [];
		data.forEach((item) => {
			this.layerLoadMap[item.layer_cate] = {
				layerCate: item.layer_cate,
				layerType: item.layer_type,
				cols: this.utilityService.arrayToObjectByFirstProp(item.cols)
			};
		});
		console.log(this.layerLoadMap);
	}
// ------------------------------------------

	public getUsStates() {
		return new Promise<USState[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/usStates';
			this.requestService.requestGet(url, null,
				(rsp) => {
					const states: USState[] = [];
					if (rsp && rsp.data) {
						rsp.data.forEach(d => {
							states.push({
								state: d.hsf_state,
								stateFp: d.hsf_statefp,
								code: d.hsf_usps_code,
								bbox: d.hsf_bbox
							});
						});
					}
					this.usStates = states;
					resolve(states);
				}, (err) => {
					reject(err);
				}, true);
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/common-services/appinfo.service.ts